// @ts-nocheck
import React, { useState } from 'react';
import {
  LuRefreshCw,
  LuChevronDown,
  LuChevronUp,
  LuClock,
  LuHash,
  LuLoader,
  LuInbox,
  LuXCircle,
  LuWifi,
  LuWifiOff,
  LuHeartPulse,
  LuAlertCircle,
  LuActivity,
  LuServer,
  LuNetwork,
  LuTimer,
  LuLink2,
} from 'react-icons/lu';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_SEMANTIC,
  APP_HEX,
} from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { IconBadge } from '@/components/atoms/IconBadge';
import PillCell, {
  CONNECTION_LOG_EVENT_PILL_MAP,
  SMART_COOLER_SYSTEM_STATE_PILL_MAP,
} from '@/components/organism/StatusPills/PillCell';
import { DetailsList } from '@/components/elements/DetailsList';
import {
  ExpandableCard,
  ExpandablePanel,
} from '@/components/elements/ExpandablePanel';
import AppButton from '@/components/atoms/AppButton';
import { IConnectionLog } from '../smartCooler.interface';

// ─── Event config ─────────────────────────────────────────────────────────────

function eventCfg(event) {
  const e = (event ?? '').toUpperCase();
  if (e === 'CONNECTED')
    return {
      label: 'Connected',
      iconColor: APP_HEX.success,
      icon: <LuWifi className="w-4 h-4" />,
      ring: 'ring-emerald-300/40 dark:ring-emerald-700/30',
      glow: 'shadow-emerald-100 dark:shadow-emerald-900/20',
    };
  if (e === 'DISCONNECTED')
    return {
      label: 'Disconnected',
      iconColor: APP_HEX.danger,
      icon: <LuWifiOff className="w-4 h-4" />,
      ring: 'ring-red-300/40 dark:ring-red-700/30',
      glow: 'shadow-red-100 dark:shadow-red-900/20',
    };
  if (e === 'HEARTBEAT')
    return {
      label: 'Heartbeat',
      iconColor: APP_HEX.sky,
      icon: <LuHeartPulse className="w-4 h-4" />,
      ring: 'ring-sky-300/40 dark:ring-sky-700/30',
      glow: 'shadow-sky-100 dark:shadow-sky-900/20',
    };
  if (e === 'FAILED_HEARTBEAT')
    return {
      label: 'Failed Heartbeat',
      iconColor: APP_HEX.warning,
      icon: <LuAlertCircle className="w-4 h-4" />,
      ring: 'ring-amber-300/40 dark:ring-amber-700/30',
      glow: 'shadow-amber-100 dark:shadow-amber-900/20',
    };
  return {
    label: event ?? 'Unknown',
    iconColor: APP_HEX.info,
    icon: <LuActivity className="w-4 h-4" />,
    ring: 'ring-indigo-300/40 dark:ring-indigo-700/30',
    glow: 'shadow-indigo-100 dark:shadow-indigo-900/20',
  };
}

function getLogId(log) {
  return log.ccLogId ?? log.logId ?? log.id ?? '';
}
function shortId(id) {
  return id ? id.slice(0, 8) : '—';
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

const SkeletonRow = () => {
  return (
    <div
      className={`rounded-2xl border ${APP_BORDER.default} ${APP_BG.base} p-4 animate-pulse`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl shrink-0 ${APP_BG.muted}`} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className={`h-4 w-28 rounded-lg ${APP_BG.muted}`} />
            <div className={`h-4 w-16 rounded-full ${APP_BG.muted}`} />
          </div>
          <div className="flex gap-3">
            <div className={`h-3 w-24 rounded ${APP_BG.elevated}`} />
            <div className={`h-3 w-32 rounded ${APP_BG.elevated}`} />
          </div>
        </div>
        <div className={`h-5 w-20 rounded-full ${APP_BG.muted}`} />
      </div>
    </div>
  );
};

// ─── Connection log card ──────────────────────────────────────────────────────

const ConnCard = ({ log, expanded, onToggle }) => {
  const ev = eventCfg(log.event ?? log.eventType ?? '');
  const logId = getLogId(log);
  const cm = log.meta?.coolerMeta ?? {};
  const connMeta = log.meta?.connectionMeta ?? {};

  const coolerMetaItems = Object.entries(cm)
    .filter(([, v]) => v != null)
    .map(([k, v]) => ({
      label: k,
      value: String(v),
      mono: ['deviceId', 'deviceSn', 'nativeId', 'readerSn'].includes(k),
    }));

  const connMetaItems = [
    {
      label: 'Last System State',
      value: connMeta.systemState ? (
        <PillCell
          value={connMeta.systemState}
          map={SMART_COOLER_SYSTEM_STATE_PILL_MAP}
          dot
          size="text-[11px]"
        />
      ) : null,
      hidden: !connMeta.systemState,
    },
    {
      label: 'Last Connected',
      value: connMeta.lastConnectedDtm
        ? getDateTime(connMeta.lastConnectedDtm)
        : null,
      hidden: !connMeta.lastConnectedDtm,
    },
    {
      label: 'Last Disconnected',
      value: connMeta.lastDisconnectedDtm
        ? getDateTime(connMeta.lastDisconnectedDtm)
        : null,
      hidden: !connMeta.lastDisconnectedDtm,
    },
    {
      label: 'Last Heartbeat',
      value: connMeta.lastHeartbeatDtm
        ? getDateTime(connMeta.lastHeartbeatDtm)
        : null,
      hidden: !connMeta.lastHeartbeatDtm,
    },
    {
      label: 'Failed Heartbeat',
      value: connMeta.lastFailedHeartbeatDtm
        ? getDateTime(connMeta.lastFailedHeartbeatDtm)
        : null,
      hidden: !connMeta.lastFailedHeartbeatDtm,
    },
  ];

  return (
    <ExpandableCard
      expanded={expanded}
      onToggle={onToggle}
      testId={tid(TID.CONN_LOGS_EXPAND_ROW(logId))}
      accentColor={ev.iconColor}
      expandedRing={`ring-2 ${ev.ring}`}
      expandedShadow={`shadow-lg ${ev.glow}`}
      showChevron={false}
      headerClassName="pl-5 pr-4 py-3.5"
      header={
        <div className="flex items-center gap-3.5 w-full">
          <IconBadge
            icon={ev.icon}
            size="md"
            variant="light"
            color={ev.iconColor}
          />

          <div className="flex-1 min-w-0">
            <p
              className={`text-[13px] font-semibold leading-snug ${APP_TEXT.default}`}
            >
              {ev.label}
            </p>
            {log.message && (
              <p
                className={`text-[11px] mt-0.5 leading-snug ${APP_TEXT.muted}`}
              >
                {log.message}
              </p>
            )}
            <div
              className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] ${APP_TEXT.muted}`}
            >
              {logId && (
                <span className="inline-flex items-center gap-1 font-mono">
                  <LuHash className="w-2.5 h-2.5 opacity-60" />
                  {shortId(logId)}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <LuClock className="w-2.5 h-2.5 opacity-60" />
                {getDateTime(log.createdOn ?? '')}
              </span>
              {cm.deviceId && (
                <span className="inline-flex items-center gap-1 font-mono">
                  <LuServer className="w-2.5 h-2.5 opacity-60 shrink-0" />
                  {cm.deviceId}
                </span>
              )}
              {log.duration != null && (
                <span className="inline-flex items-center gap-1 font-mono">
                  <LuTimer className="w-2.5 h-2.5 opacity-60" />
                  {log.duration}s
                </span>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-col items-end gap-1.5 min-w-[100px]">
            <div className="flex items-center gap-2">
              <PillCell
                value={(log.event ?? log.eventType ?? '').toUpperCase()}
                map={CONNECTION_LOG_EVENT_PILL_MAP}
                dot
                size="text-[11px]"
              />
              <div
                className={[
                  'w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm',
                  expanded
                    ? 'bg-indigo-500 text-white'
                    : `${APP_BG.muted} ${APP_TEXT.soft} group-hover:bg-indigo-100 dark:group-hover:bg-indigo-950/50 group-hover:text-indigo-500`,
                ].join(' ')}
              >
                {expanded ? (
                  <LuChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <LuChevronDown className="w-3.5 h-3.5" />
                )}
              </div>
            </div>
            <span className={`text-[10px] tabular-nums ${APP_TEXT.muted}`}>
              {getDateTimeAgo(log.createdOn ?? '')}
            </span>
          </div>
        </div>
      }
    >
      {/* ── Expanded panel ── */}
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${APP_BG.base}`}>
        {/* ── Core details ── */}
        <div
          className={`px-5 py-4 border-b lg:border-b-0 lg:border-r ${APP_BORDER.soft} flex flex-col gap-3`}
        >
          <DetailsList
            title="Details"
            titleIcon={<LuNetwork className="w-3.5 h-3.5" />}
            items={[
              { label: 'Log ID', value: logId, mono: true, hidden: !logId },
              {
                label: 'Event',
                value: (
                  <PillCell
                    value={(log.event ?? log.eventType ?? '').toUpperCase()}
                    map={CONNECTION_LOG_EVENT_PILL_MAP}
                    dot
                    size="text-[11px]"
                  />
                ),
              },
              {
                label: 'Timestamp',
                value: (
                  <div className="text-right">
                    <p
                      className={`text-[11px] font-semibold tabular-nums ${APP_TEXT.soft}`}
                    >
                      {getDateTime(log.createdOn ?? '')}
                    </p>
                    <p className={`text-[9px] tabular-nums ${APP_TEXT.subtle}`}>
                      {getDateTimeAgo(log.createdOn ?? '')}
                    </p>
                  </div>
                ),
              },
              {
                label: 'Duration',
                value: (
                  <span
                    className={`text-[12px] font-mono font-bold tabular-nums ${APP_TEXT.default}`}
                  >
                    {log.duration}s
                  </span>
                ),
                hidden: log.duration == null,
              },
            ]}
          />
          {log.message && (
            <div
              className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border ${APP_SEMANTIC.warning.bg} ${APP_SEMANTIC.warning.border}`}
            >
              <LuAlertCircle
                className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${APP_SEMANTIC.warning.text}`}
              />
              <p className={`text-[11px] leading-relaxed ${APP_TEXT.soft}`}>
                {log.message}
              </p>
            </div>
          )}
        </div>

        {/* ── Meta ── */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {coolerMetaItems.length > 0 && (
            <DetailsList
              title="Cooler Meta"
              titleIcon={<LuServer className="w-3.5 h-3.5" />}
              items={coolerMetaItems}
            />
          )}
          {connMetaItems.some((r) => !r.hidden) && (
            <DetailsList
              title="Connection Meta"
              titleIcon={<LuLink2 className="w-3.5 h-3.5" />}
              items={connMetaItems}
            />
          )}
        </div>
      </div>
    </ExpandableCard>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ViewSCConnectionLogsTab = ({
  coolerId,
  logs,
  loading,
  loadingMore,
  error,
  totalRecords,
  initialized,
  onInit,
  onLoadMore,
  onRefresh,
}) => {
  const [expandedId, setExpandedId] = useState(null);

  React.useEffect(() => {
    if (!initialized) onInit();
  }, []);

  const hasMore = logs.length < totalRecords;

  const toolbar = (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 ${APP_BG.surface}`}
    >
      <div className="flex items-center gap-2.5">
        <IconBadge
          icon={<LuNetwork className="w-3.5 h-3.5" />}
          size="sm"
          variant="solid"
        />
        <div>
          <p
            className={`text-[13px] font-bold leading-none ${APP_TEXT.default}`}
          >
            Connection Logs
          </p>
          <p className={`text-[11px] mt-1 ${APP_TEXT.muted}`}>
            {logs.length === totalRecords
              ? `${totalRecords} total records`
              : `${logs.length} of ${totalRecords} loaded`}
          </p>
        </div>
      </div>
      <AppButton
        data-testid={tid(TID.CONN_LOGS_REFRESH_BTN)}
        variant="neutral"
        size="sm"
        icon={<LuRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />}
        onClick={onRefresh}
        disabled={loading}
      >
        Refresh
      </AppButton>
    </div>
  );

  return (
    <ExpandablePanel
      items={logs}
      getItemId={(log) => getLogId(log)}
      renderItem={(log, expanded, onToggle) => (
        <ConnCard log={log} expanded={expanded} onToggle={onToggle} />
      )}
      loading={loading}
      loadingMore={loadingMore}
      skeletonCount={5}
      renderSkeleton={() => <SkeletonRow />}
      error={error}
      errorTitle="Failed to load connection logs"
      onRetry={onRefresh}
      emptyIcon={
        <div className="relative">
          <LuNetwork className={`w-7 h-7 ${APP_TEXT.subtle}`} />
          <div
            className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ring-2 ${APP_BG.base} bg-indigo-100 dark:bg-indigo-950/60`}
          >
            <LuActivity className="w-3 h-3 text-indigo-500" />
          </div>
        </div>
      }
      emptyTitle="No connection logs yet"
      emptyDescription="Connection events for this device will appear here."
      emptyAction={
        <AppButton
          data-testid={tid(TID.CONN_LOGS_REFRESH_BTN)}
          variant="neutral"
          size="sm"
          icon={<LuRefreshCw className="w-3 h-3" />}
          onClick={onRefresh}
        >
          Refresh
        </AppButton>
      }
      toolbar={toolbar}
      hasMore={hasMore}
      totalRecords={totalRecords}
      loadedCount={logs.length}
      onLoadMore={onLoadMore}
      expandedId={expandedId}
      onExpandChange={setExpandedId}
      scrollable
    />
  );
};

export default ViewSCConnectionLogsTab;
